import { ExternalLink } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@akuna/ui";
import { Alert, AlertDescription, AlertTitle } from "@akuna/ui";
import { AlertCircle, Info } from "lucide-react";
import { useTranslation } from "react-i18next";

interface SIPEAGROGuideDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onConfirm: () => void;
    onCancel: () => void;
}

const SIPEAGRO_URL = "https://sistemasweb.agricultura.gov.br/pages/SIPEAGRO.html";
const SIPEAGRO_MANUAL_URL = "https://www.gov.br/agricultura/pt-br/assuntos/insumos-agropecuarios/insumos-pecuarios/produtos-veterinarios/cadastro-de-medicos-veterinarios/arquivos/PVMANUALPARAEMISSAODENOTIFICACAOversao3.pdf";

export function SIPEAGROGuideDialog({
    open,
    onOpenChange,
    onConfirm,
    onCancel,
}: SIPEAGROGuideDialogProps) {
    const { t } = useTranslation("prescriptions");
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Info className="h-5 w-5 text-blue-500" />
                        {t("sipeagro.title")}
                    </DialogTitle>
                    <DialogDescription>
                        {t("sipeagro.description")}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <Alert>
                        <AlertCircle className="h-4 w-4" />
                        <AlertTitle>{t("sipeagro.warningTitle")}</AlertTitle>
                        <AlertDescription>
                            {t("sipeagro.warningDescription")}
                        </AlertDescription>
                    </Alert>

                    <div className="space-y-3">
                        <h3 className="font-semibold text-sm">{t("sipeagro.stepsTitle")}</h3>
                        <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                            <li>{t("sipeagro.step1")}</li>
                            <li>{t("sipeagro.step2")}</li>
                            <li>{t("sipeagro.step3")}</li>
                            <li>{t("sipeagro.step4")}</li>
                            <li>{t("sipeagro.step5")}</li>
                        </ol>
                    </div>

                    <div className="space-y-3">
                        <div className="space-y-2">
                            <p className="text-sm font-medium">{t("sipeagro.linkLabel")}</p>
                            <a
                                href={SIPEAGRO_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-primary hover:underline text-sm"
                            >
                                <ExternalLink className="h-4 w-4" />
                                {SIPEAGRO_URL}
                            </a>
                        </div>
                        <div className="space-y-2">
                            <p className="text-sm font-medium">{t("sipeagro.manualLabel")}</p>
                            <a
                                href={SIPEAGRO_MANUAL_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-primary hover:underline text-sm"
                            >
                                <ExternalLink className="h-4 w-4" />
                                {t("sipeagro.manualLink")}
                            </a>
                        </div>
                    </div>

                    {/* <Alert variant="default">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription className="text-sm">
                            <strong>Importante:</strong> Estamos trabalhando para que o sistema emita receitas controladas diretamente.
                            Enquanto isso, você deve usar o sistema do MAPA para emitir receitas controladas.
                        </AlertDescription>
                    </Alert> */}
                </div>

                <DialogFooter className="flex-col sm:flex-row gap-2">
                    <Button variant="outline" onClick={onCancel} className="w-full sm:w-auto">
                        {t("sipeagro.close")}
                    </Button>
                    <Button onClick={onConfirm} className="w-full sm:w-auto">
                        {t("sipeagro.understood")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
